import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import dayjs from "dayjs";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";
import {
  clearAccountingTransaction,
  clearAcctTransactionError,
  clearDownloadInvoice,
  clearInvoiceTransaction,
  clearInvoiceTransactionError,
  requestCreateAcctTransaction,
  requestDownloadInvoice,
  requestGetAccountingTransaction,
  requestGetBusiness,
  requestListAccount,
  requestListBusinessTax,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import { TAX_DIRECTORY_LIMIT } from "../../Constants/taxCategories";
import { Button } from "../../Components/Button";
import Divider from "../../Components/Divider";
import { Alert } from "../../Components/Alert";
import TableSkeleton from "../../Components/TableSkeleton";
import { Modal } from "../../Components/Modal";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Select from "react-select";
import InputSkeleton from "../../Components/InputSkeleton";
import {
  classNames,
  getAccountingFileName,
  getErrorMessages,
  getPeriodClosedError,
  parseTypedClosedDate,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import {
  calculateItemTaxBreakdown,
  describeTaxRow,
  senDecimalProps,
} from "../../Utils/AccountingUtil";
import { useInvoiceTotals } from "../../Hooks/useInvoiceTotals";
import { isTransactionPaid } from "../../Utils/PaymentUtils";
import { downloadFakturKeluaranXml } from "../../Utils/FakturXmlUtil";
import FakturXmlExportModal from "../../Components/v2/Accounting/FakturXmlExportModal";

const InvoiceDetail = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: transactionIdParam } = useParams();

  const accountingDispatch = useAccountingDispatch();
  const businessDispatch = useBusinessTransactionDispatch();
  const {
    transaction,
    loading,
    error,
    transactionID,
    submitLoading,
    submitError,
    accounts,
    accountLoading,
    downloadInvoice,
    downloadInvoiceLoading,
    downloadInvoiceError,
  } = useAccountingState();
  const { business } = useBusinessTransactionState();
  const { user } = useAuthState();

  const [showPaymentModal, setshowPaymentModal] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [showDownloadInvoiceError, setshowDownloadInvoiceError] =
    useState(false);

  const [selectedAccount, setselectedAccount] = useState(null);
  const [paymentDate, setpaymentDate] = useState(new Date());
  const [totalPaid, settotalPaid] = useState("");
  const [paymentDescription, setpaymentDescription] = useState("");
  const [debitAccount, setDebitAccount] = useState(null);

  const [submitted, setsubmitted] = useState(false);

  // Two independent locks. Editing the invoice follows the invoice's own date;
  // recording a payment follows the payment date, which the user may set to
  // today even when the invoice itself sits inside a closed period.
  const {
    minDate: periodMinDate,
    isDateClosed,
    isClosed: periodClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(transaction?.businessID, { locale: i18n.language });
  const invoiceLocked = isDateClosed(transaction?.createdAt);
  const paymentDateLocked = isDateClosed(paymentDate);

  const [showXmlModal, setShowXmlModal] = useState(false);

  const [downloading, setdownloading] = useState(false);
  const downloadRef = useRef(null);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);

  const [dateError, setdateError] = useState("");
  const [totalError, settotalError] = useState("");
  const [selectedAccountError, setselectedAccountError] = useState("");

  useEffect(() => {
    return () => {
      clearInvoiceTransaction(accountingDispatch);
      clearInvoiceTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (transaction?.id) {
      requestGetBusiness(businessDispatch, {
        id: transaction.businessID,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id]);

  useEffect(() => {
    if (transactionIdParam) {
      requestGetAccountingTransaction(accountingDispatch, {
        id: transactionIdParam,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionIdParam]);

  useEffect(() => {
    if (transaction && Object.keys(transaction).length > 0) {
      if (!transaction.invoiceNumber) {
        navigate(`/accounting-transaction/create?id=${transactionIdParam}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.invoiceNumber]);

  // An invoice line stores one tax relation, so the line alone cannot name a
  // second tax on it — a withheld PPh 23 read "Pajak tidak tersedia (2%)"
  // while the tax was alive and in use. The business's tax list supplies the
  // rest of the names; the terms charged still come from the line.
  const taxDispatch = useBusinessTaxDispatch();
  const { businessTaxes } = useBusinessTaxState();

  useEffect(() => {
    if (transaction?.businessID) {
      // Explicit page and limit: the action defaults to ten per page and
      // stops at the first non-empty one, so a business with more taxes than
      // that would leave the directory short of the very tax we came to name.
      requestListBusinessTax(taxDispatch, {
        page: 1,
        limit: TAX_DIRECTORY_LIMIT,
        businessID: transaction.businessID,
      });
    }
  }, [transaction?.businessID, taxDispatch]);

  const taxDirectory = useMemo(
    () => new Map((businessTaxes || []).map((tax) => [tax.id, tax])),
    [businessTaxes],
  );

  // The posted total is the source of truth for a posted invoice; the line
  // arithmetic here is only a cross-check. See useInvoiceTotals.
  const {
    calculations,
    total: totalWithTax,
    totalPayment,
    balanceDue,
    settlementAmount,
  } = useInvoiceTotals(transaction, taxDirectory);
  // Same rule as Balance Due, so Pay is never disabled-as-paid next to a
  // remainder, nor offered against a remainder of Rp0.
  const isPaid = isTransactionPaid(transaction);

  const taxes = calculations.taxes;
  const subtotal = calculations.subtotal;
  const discountAmount = calculations.discount;

  // Pre-fill the payment with what is still owed. A settled invoice leaves the
  // field empty rather than falling back to the full total, which used to be
  // written as a bare number where the rest of the screen reads {floatValue}.
  useEffect(() => {
    settotalPaid(
      settlementAmount > 0
        ? { floatValue: settlementAmount, value: settlementAmount }
        : "",
    );
  }, [settlementAmount, showPaymentModal]);

  useEffect(() => {
    if (transaction?.businessID) {
      requestListAccount(accountingDispatch, {
        businessID: transaction?.businessID,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.businessID]);

  useEffect(() => {
    if (transaction?.id) {
      const { items } = transaction.accounts || {};
      if (items) {
        items.forEach((account) => {
          if (account.debit > 0) {
            const { account: debitAccount } = account;
            setDebitAccount({
              ...debitAccount,
              label: debitAccount.name,
              value: debitAccount.id,
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.id]);

  useEffect(() => {
    if (submitted) {
      const isPaymentDateValid = Boolean(paymentDate);
      const isTotalPaidValid = totalPaid?.value > 0;
      const isSelectedAccountValid = Boolean(selectedAccount?.value);

      if (isPaymentDateValid) {
        setErrorMessage("date", "");
      } else {
        setErrorMessage("date", "Date is required.");
      }

      if (isTotalPaidValid) {
        setErrorMessage("totalPaid", "");
      } else {
        setErrorMessage("totalPaid", "Total Paid is required.");
      }

      if (isSelectedAccountValid) {
        setErrorMessage("account", "");
      } else {
        setErrorMessage("account", "Account is required.");
      }
    }
  }, [paymentDate, totalPaid, selectedAccount, submitted]);

  useEffect(() => {
    if (transactionID) {
      setshowPaymentModal(false);
      setshowSuccessModal(true);
    }
  }, [transactionID]);

  useEffect(() => {
    if (downloadInvoiceError) {
      setshowDownloadInvoiceError(true);
    }
  }, [downloadInvoiceError]);

  useEffect(() => {
    if (downloadInvoice?.key) {
      downloadFile(downloadInvoice.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadInvoice]);

  const navigateToAccounting = () => {
    setshowSuccessModal(false);
    clearAccountingTransaction(accountingDispatch);
    if (!submitError) {
      if (transactionID) {
        navigate("/v2/accounting");
      }
    }
  };

  const validateInput = () => {
    let currentValidPaymentDate = true;
    let currentValidTotalPaid = true;
    let currentValidSelectedAccount = true;

    const isPaymentDateValid = Boolean(paymentDate);
    const isTotalPaidValid = totalPaid?.value > 0;
    const isSelectedAccountValid = Boolean(selectedAccount?.value);

    currentValidPaymentDate = isPaymentDateValid;
    currentValidTotalPaid = isTotalPaidValid;
    currentValidSelectedAccount = isSelectedAccountValid;

    return (
      currentValidPaymentDate &&
      currentValidTotalPaid &&
      currentValidSelectedAccount
    );
  };

  const handlePayment = () => {
    setsubmitted(true);

    if (!validateInput()) return;
    if (paymentDateLocked) return;

    const input = {
      created_at: paymentDate.toISOString(),
      total: totalPaid.floatValue,
      accounts: [],
      description: paymentDescription,
      paid: true,
      business_id: transaction.businessID,
      partner_id: transaction.partnerID,
      parent_transaction_id: transactionIdParam,
    };

    const debitAccountValue = debitAccount?.value;
    const selectedAccountValue = selectedAccount?.value;

    input.accounts.push({
      id: selectedAccountValue,
      debit: input.total,
      credit: 0,
    });

    input.accounts.push({
      id: debitAccountValue,
      debit: 0,
      credit: input.total,
    });

    input.type = "RECEIVABLE_PAYMENT";

    requestCreateAcctTransaction(accountingDispatch, { input });
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "date":
        setdateError(message);
        break;
      case "totalPaid":
        settotalError(message);
        break;
      case "account":
        setselectedAccountError(message);
        break;
      default:
        break;
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  const onDownloadInvoice = () => {
    if (transaction?.id) {
      requestDownloadInvoice(accountingDispatch, {
        transactionId: transaction.id,
      });
    }
  };

  const onExportXmlClick = () => {
    if (!transaction?.id) return;

    setShowXmlModal(true);
  };

  const onSubmitXml = (options) => {
    downloadFakturKeluaranXml(transaction, options);
    setShowXmlModal(false);
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
      clearDownloadInvoice(accountingDispatch);
    }
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
        </div>
      </div>
    </Modal>
  );

  const resetPaymentModal = () => {
    setpaymentDate(new Date());
    settotalPaid("");
    setselectedAccount(null);
    setdateError("");
    settotalError("");
    setselectedAccountError("");
  };

  const renderSkeletonLoading = () => {
    return (
      <div className="space-y-6 px-4 py-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InputSkeleton rows={6} colSpan={1} />
        </div>
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        #
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Product")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Quantity")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Price")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Subtotal")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Taxes")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("DPP")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("totalTax")}
                      </th>
                    </tr>
                  </thead>
                  <TableSkeleton rows={3} columns={8} />
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 justify-between">
          <div className="animate-pulse">
            <div className="flex-1 space-y-2">
              <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
              <div className="block h-6 bg-gray-300 rounded-md"></div>
            </div>
          </div>
          <div className="flex flex-col gap-1 col-span-2 sm:col-span-1 text-sm sm:col-end-5">
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <div className="flex justify-between">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <Divider />
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
            <div className="flex justify-between font-medium">
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
              <div className="w-20 h-4 bg-gray-300 rounded-md animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentModal = () => {
    return (
      <Modal
        title={t("Pay Receivable")}
        show={showPaymentModal}
        onClose={() => {
          setshowPaymentModal(false);
          setsubmitted(false);
          resetPaymentModal();
          clearAcctTransactionError(accountingDispatch);
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={submitLoading}
              loading={submitLoading}
              onClick={(e) => {
                e.preventDefault();
                handlePayment();
              }}
              className="w-24 shrink-0"
              title="Pay"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          <PeriodClosedNotice error={submitError} onRefresh={refetchPeriod} />
          {submitError && !getPeriodClosedError(submitError) && (
            <Alert
              title="Error"
              type="error"
              message={submitError?.message || "submitError Pay Transaction."}
              errors={submitError?.errors}
            />
          )}
          <div>
            <label
              htmlFor="paymentDate"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Date")}
            </label>
            <ReactDatePicker
              name="paymentDate"
              className={classNames(
                dateError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
              selected={paymentDate}
              onChange={setpaymentDate}
              placeholderText={t("Select Date")}
              dateFormat="dd/MM/yyyy"
              minDate={periodMinDate}
              // A typed closed date is kept so the hint below names the period;
              // the picker alone would drop it silently.
              onChangeRaw={(event) => {
                const typed = parseTypedClosedDate(
                  event?.target?.value,
                  "dd/MM/yyyy",
                  isDateClosed,
                );
                if (typed) setpaymentDate(typed);
              }}
            />
            {periodClosed && (
              <p className="mt-1 text-xs text-amber-700">
                {paymentDateLocked
                  ? t("accounting.period.hints.dateLocked", {
                      date: closedThroughLabel,
                    })
                  : t("accounting.period.banner.closedThrough", {
                      date: closedThroughLabel,
                    })}
              </p>
            )}
            {dateError && (
              <div className="mt-1 text-red-600 text-sm">{t(dateError)}</div>
            )}
          </div>
          <div>
            <label
              htmlFor="totalPaid"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Total Paid")}
            </label>
            <NumericFormat
              type="text"
              name="totalPaid"
              id="totalPaid"
              value={totalPaid.value || ""}
              placeholder={t("Enter Total Transaction")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              onValueChange={(value) => settotalPaid(value)}
              className={classNames(
                totalError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {totalError && (
              <div className="mt-1 text-red-600 text-sm">{t(totalError)}</div>
            )}
          </div>
          <div>
            <label
              htmlFor="selectedAccount"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Payment Account")}
            </label>
            <Select
              name="selectedAccount"
              className={classNames(
                selectedAccountError
                  ? "border border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
              classNamePrefix="select2-selection"
              options={accounts
                .filter((account) => account.category === "CASH_AND_BANK")
                .map((account) => ({
                  value: account.id,
                  label: account.name,
                }))}
              value={selectedAccount}
              onChange={setselectedAccount}
              placeholder={t("Choose Account")}
              isDisabled={accountLoading}
              isLoading={accountLoading}
              maxMenuHeight={100}
            />
            {selectedAccountError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(selectedAccountError)}
              </div>
            )}
          </div>
          <div>
            <label
              htmlFor="paymentDescription"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Note")}
            </label>
            <input
              type="text"
              name="paymentDescription"
              value={paymentDescription || ""}
              placeholder={t("Enter Transaction Note")}
              onChange={(e) => setpaymentDescription(e.target.value)}
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </Modal>
    );
  };

  if (!transaction || error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message || "Invoice not found."}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PeriodLockBanner businessID={transaction?.businessID} />
      <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow sm:overflow-hidden rounded-md sm:rounded-lg sm:min-h-[30rem]">
        {transactionID && (
          <ModalConfirm
            open={showSuccessModal}
            setOpen={(open) => {
              setshowSuccessModal(open);
            }}
            title="Success"
            description={"Your payment transaction was successful."}
            type="SUCCESS"
            onConfirm={navigateToAccounting}
          />
        )}
        {downloadInvoiceError && (
          <ModalConfirm
            open={showDownloadInvoiceError}
            type="ALERT"
            setOpen={(open) => {
              setshowDownloadInvoiceError(open);
            }}
            title="Error"
            description={getErrorMessages(downloadInvoiceError)}
            onConfirm={() => {
              clearDownloadInvoice(accountingDispatch);
            }}
          />
        )}
        {renderFileModal()}
        {renderDownloadError()}
        {renderPaymentModal()}
        {loading ? (
          renderSkeletonLoading()
        ) : (
          <div className="space-y-6 px-4 py-5 sm:p-6">
            {transaction?.id &&
              business?.imageUrl &&
              user.membership === "PREMIUM" && (
                <div>
                  <img
                    src={business.imageUrl}
                    alt={t("Profile")}
                    className="rounded-lg object-contain w-24 h-24 max-h-[100px]"
                  />
                </div>
              )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-500">
                  {t("Business Name")}
                </span>
                <div className="text-sm font-bold text-gray-900">
                  {transaction.business?.name}
                </div>
                <div className="text-sm text-gray-900">
                  {transaction.business?.address}
                </div>
              </div>

              <div className="w-full">
                <span className="text-sm text-gray-500">
                  {t("Transaction Date")}
                </span>
                <div className="text-sm font-bold text-gray-900">
                  {dayjs(transaction.createdAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY")}
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-500">{t("Buyer")}</span>
                <div className="text-sm font-bold text-gray-900">
                  {transaction.partner?.name}
                </div>
                <div className="text-sm text-gray-900">
                  {transaction.partner?.address}
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-500">{t("Due Date")}</span>
                <div className="text-sm font-bold text-gray-900">
                  {dayjs(transaction.paymentDueAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY")}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">
                  {t("Invoice Number")}
                </span>
                <div className="text-sm font-bold text-gray-900">
                  {transaction.invoiceNumber || "-"}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">
                  {t("Reference Number")}
                </span>
                <div className="text-sm font-bold text-gray-900">
                  {transaction.referenceNumber || "-"}
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
                  <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            #
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("Product")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("Quantity")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                            {t("Price")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                            {t("Subtotal")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                            {t("Taxes")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                            {t("DPP")}
                          </th>
                          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                            {t("totalTax")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {transaction?.items?.items?.map((item, index) => {
                          const { product, price, quantity } = item;

                          // Satu sumber perhitungan dgn ekspor XML.
                          const breakdown = calculateItemTaxBreakdown(
                            item,
                            subtotal,
                            discountAmount,
                            transaction?.discount?.applyAfterTax || false,
                            taxDirectory,
                            transaction?.taxRounding,
                          );
                          const productSubtotal = breakdown.itemTotal;
                          const itemTaxBase = breakdown.otherTaxBase;
                          // Net of anything withheld, so the line adds up to
                          // what is actually billed for it.
                          const itemTax = breakdown.totalTax;
                          // Every tax on the line. An entry survives for a tax
                          // that no longer resolves, so the row names it
                          // instead of reading as untaxed.
                          const itemTaxes = breakdown.taxes;

                          return (
                            <tr key={index}>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap">
                                {index + 1}
                              </td>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap sm:w-48">
                                <p className="font-medium text-gray-900">
                                  {product?.name ||
                                    (item.productID
                                      ? t("products.unavailable")
                                      : "-")}
                                </p>
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {quantity}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  value={price}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  displayType={"text"}
                                  prefix={"Rp"}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-right">
                                <NumericFormat
                                  value={productSubtotal || 0}
                                  {...senDecimalProps(productSubtotal || 0)}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  displayType="text"
                                  prefix={"Rp"}
                                  className="block w-full text-sm"
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {itemTaxes.length ? (
                                  <div className="flex flex-col gap-0.5">
                                    {itemTaxes.map((entry, entryIndex) => (
                                      <span
                                        key={entry.id || entryIndex}
                                        className={clsx(
                                          entry.withholding && "text-amber-700",
                                          !entry.name && "text-gray-500 italic",
                                        )}
                                      >
                                        {entry.name || t("taxes.unavailable")}
                                        {entry.withholding &&
                                          ` · ${t("taxes.badges.withheld")}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  // DPP is a VAT figure; a line that only
                                  // has PPh withheld has none.
                                  value={
                                    itemTaxes.some((entry) => !entry.withholding)
                                      ? itemTaxBase
                                      : "-"
                                  }
                                  {...senDecimalProps(itemTaxes.some((entry) => !entry.withholding) ? itemTaxBase : "-")}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  displayType="text"
                                  prefix={"Rp"}
                                  className="block w-full text-sm"
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  value={itemTax || 0}
                                  {...senDecimalProps(itemTax || 0)}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  displayType="text"
                                  prefix={"Rp"}
                                  className="block w-full text-sm"
                                />
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td className="p-6"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 justify-between">
              <div className="col-span-2">
                <span className="text-sm text-gray-500">{t("Message")}</span>
                <div className="text-sm font-bold text-gray-900">
                  {transaction?.description || "-"}
                </div>
              </div>
              <div className="flex flex-col gap-1 col-span-2 sm:col-span-1 text-sm sm:col-end-5">
                <div className="flex justify-between font-medium">
                  <span>{t("Subtotal")}</span>
                  <NumericFormat
                    value={subtotal || 0}
                    {...senDecimalProps(subtotal || 0)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
                {transaction?.discount && (
                  <div className="flex justify-between text-green-600">
                    <span>{t("Discount")}</span>
                    <NumericFormat
                      value={-(discountAmount || 0)}
                      {...senDecimalProps(-(discountAmount || 0))}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                      prefix={"Rp"}
                    />
                  </div>
                )}
                {taxes.length > 0 &&
                  taxes.map((tax) => {
                    // A tax that no longer resolves has no name, but it is
                    // still charged, so the row cannot go unlabelled. A withheld
                    // one reads as a deduction, matching the PDF.
                    const row = describeTaxRow(tax, t);
                    return (
                      <div
                        key={tax.id}
                        className={clsx(
                          "flex justify-between gap-2",
                          row.withholding && "text-amber-700",
                        )}
                      >
                        <span>{row.label}</span>
                        <NumericFormat
                          type="text"
                          name="totalAmount"
                          id="totalAmount"
                          value={row.value}
                          {...senDecimalProps(row.value)}
                          thousandSeparator="."
                          decimalSeparator=","
                          displayType="text"
                          prefix={"Rp"}
                        />
                      </div>
                    );
                  })}
                {calculations.roundingAmount ? (
                  <div className="flex justify-between">
                    <span>{t("invoices.fields.rounding")}</span>
                    <NumericFormat
                      value={calculations.roundingAmount}
                      {...senDecimalProps(calculations.roundingAmount)}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="text"
                      prefix={"Rp"}
                    />
                  </div>
                ) : null}
                <Divider />
                <div className="flex justify-between font-medium">
                  <span>{t("Total")}</span>
                  <NumericFormat
                    value={totalWithTax || 0}
                    {...senDecimalProps(totalWithTax || 0)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
                <div className="flex justify-between">
                  <span>{t("Payment")}</span>
                  <NumericFormat
                    type="text"
                    value={totalPayment || 0}
                    {...senDecimalProps(totalPayment || 0)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
                <div className="flex justify-between font-medium">
                  <span>{t("Balance Due")}</span>
                  <NumericFormat
                    type="text"
                    value={balanceDue || 0}
                    {...senDecimalProps(balanceDue || 0)}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="text"
                    prefix={"Rp"}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-between bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 sm:px-6">
          <div className="flex flex-row gap-4">
            <button
              onClick={onDownloadInvoice}
              disabled={downloadInvoiceLoading}
              type="button"
              className={classNames(
                downloadInvoiceLoading
                  ? "border-gray-300 cursor-not-allowed"
                  : "border-blue-600 hover:bg-slate-50",
                "bg-white  inline-flex justify-center items-center whitespace-nowrap px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
              )}
            >
              <DownloadIcon
                className={classNames(
                  downloadInvoiceLoading ? "text-gray-300" : "text-blue-600",
                  "w-5 h-5",
                )}
              />
            </button>
            <button
              onClick={onExportXmlClick}
              disabled={loading || !transaction?.id}
              type="button"
              className={classNames(
                loading || !transaction?.id
                  ? "border-gray-300 text-gray-300 cursor-not-allowed"
                  : "border-blue-600 text-blue-600 hover:bg-slate-50",
                "bg-white inline-flex justify-center items-center gap-2 whitespace-nowrap px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2",
              )}
            >
              <DocumentIcon className="w-5 h-5" />
              {t("invoices.detail.actions.exportXml")}
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={
                loading ||
                (submitLoading && !showPaymentModal) ||
                isPaid ||
                paymentDateLocked
              }
              title={isPaid ? t("Paid") : t("Pay")}
              onClick={() => setshowPaymentModal(true)}
              outline
            />
            <Button
              type="submit"
              className="w-full sm:w-auto"
              title={t("Edit")}
              loading={loading}
              disabled={loading || invoiceLocked}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/invoice/edit/${transaction?.id}`);
              }}
            />
          </div>
        </div>
      </div>
      {showXmlModal && (
        <FakturXmlExportModal
          open={showXmlModal}
          onClose={() => setShowXmlModal(false)}
          onSubmit={onSubmitXml}
          transaction={transaction}
        />
      )}
    </div>
  );
};

export default InvoiceDetail;
